import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import VerovioScoreViewer from './VerovioScoreViewer';
import TabView from './TabView';
import AudioPlayer from './AudioPlayer';
import { DEFAULT_BPM } from '../constants';
import './PieceViewer.css';

const PieceViewer = ({ selectedPiece, addFavorite, removeFavorite, isFavorited, inlineAudioPlayer = false, onAskStudyvarious }) => {
  const [selectedWork, setSelectedWork] = useState('');
  const [pieceDisplayName, setPieceDisplayName] = useState('');
  const [midiBlob, setMidiBlob] = useState(null);
  const [parts, setParts] = useState([]);
  const [selectedPart, setSelectedPart] = useState('');
  const [bpm, setBpm] = useState(DEFAULT_BPM);
  const [currentTime, setCurrentTime] = useState(0);
  const [allNotes, setAllNotes] = useState([]);
  const [measureTimes, setMeasureTimes] = useState([]);
  const [isNotesLoading, setIsNotesLoading] = useState(false);
  const [svgStrings, setSvgStrings] = useState([]);
  const [timemap, setTimemap] = useState([]);
  const [isCorpusPiece, setIsCorpusPiece] = useState(true);
  const [showTab, setShowTab] = useState(true);
  const [showScore, setShowScore] = useState(true);
  const audioPlayerRef = useRef(null);

  const apiUrl = process.env.REACT_APP_API_BASE || (window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://studyvarious.com/api');

  const startDelay = 5;
  const bpmScalingFactor = DEFAULT_BPM / bpm;

  useEffect(() => {
    console.log('calling to set selected piece');
    setSelectedPiece(selectedPiece);
  }, [selectedPiece]);

  // Add space key handler for play/pause
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.code === 'Space' && 
          audioPlayerRef.current && 
          !['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) {
        event.preventDefault(); // Prevent page scroll
        audioPlayerRef.current.togglePlayPause();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  const handleNoteClick = (newTime) => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.handleNoteClick(newTime);
    }
  };

  const loadPartData = async (part, workParam) => {
    if (workParam && part) {
      const requestData = { id: workParam, part };
      
      try {
        const response = await axios.post(apiUrl + '/get_score_svgs', requestData);
        setSvgStrings(response.data.svgs);
        setTimemap(response.data.timemap);
      } catch (error) {
        console.error('Error fetching SVGs/timemap:', error);
      }
      try {
        const response = await axios.post(apiUrl+'/get_midi', requestData, {
          responseType: 'blob'
        });
        setMidiBlob(response.data);
      } catch (error) {
        console.error('Error fetching MIDI:', error);
      }
      try {
        setIsNotesLoading(true);
        console.log('get notes: selectedPart:', part);
        const response = await axios.post(apiUrl+'/get_notes', requestData);
        setAllNotes(response.data.tabs);
        setMeasureTimes(response.data.measure_times);
        setCurrentTime(-startDelay);
      } catch (error) {
        console.error('Error fetching notes:', error);
      } finally {
        setIsNotesLoading(false);
      }
    }
  };

  const handlePartSelection = async (e) => {
    const part = e.target.value;
    setSelectedPart(part);
    loadPartData(part, selectedWork);
  };

  const setSelectedPiece = async (selectedPiece) => {
    if (selectedPiece) {
      try {
        let pieceId = null;
        let isCorpusPiece = false;
        
        if (selectedPiece.id && selectedPiece.title) {
          // Corpus piece with full metadata
          isCorpusPiece = true;
          setIsCorpusPiece(true);
          pieceId = selectedPiece.id;
          setSelectedWork(selectedPiece.id);
          const formattedComposer = selectedPiece.composer
              ? selectedPiece.composer.charAt(0).toUpperCase() + selectedPiece.composer.slice(1)
              : "Unknown";
          const displayText = `${formattedComposer} - ${selectedPiece.title}`;
          setPieceDisplayName(displayText);
          console.log('calling get_parts with id:', selectedPiece.id);
        } else if (selectedPiece.id) {
          // AI-generated piece (only has id)
          isCorpusPiece = false;
          setIsCorpusPiece(false);
          pieceId = selectedPiece.id;
          setSelectedWork(selectedPiece.id);
          setPieceDisplayName("AI Generated Piece");
          console.log('calling get_parts with id (AI-generated):', selectedPiece.id);
        } else {
          console.error('Selected piece has no id');
          return;
        }
        
        const requestData = {'id': pieceId};
        const response = await axios.post(apiUrl+'/get_parts', requestData);
        setParts(response.data);

        const violinPart = response.data.find(part => part.name.toLowerCase().includes('violin') || part.name.toLowerCase().includes('violino'));
        let selectedPartValue = '';
        if (violinPart) {
          selectedPartValue = violinPart.id;
        } else if (response.data.length >= 1) {
          selectedPartValue = response.data[0].id;
        }
        setSelectedPart(selectedPartValue);
        if (selectedPartValue) {
          loadPartData(selectedPartValue, pieceId);
        }
      } catch (error) {
        console.error('Error fetching parts:', error);
      }
    }
  };

  return (
    <div className={inlineAudioPlayer ? "piece-viewer piece-viewer--inline" : "piece-viewer piece-viewer--full"}>
      <div className="piece-viewer__menu">
        <label>
          <input
            type="checkbox"
            checked={showTab}
            onChange={(e) => setShowTab(e.target.checked)}
          />
          Tab
        </label>
        <label>
          <input
            type="checkbox"
            checked={showScore}
            onChange={(e) => setShowScore(e.target.checked)}
          />
          Score
        </label>
      </div>
      <div className={`piece-viewer__content ${showTab && showScore ? 'both-views' : 'single-view'}`}>
        {showTab && (
          <div className="piece-viewer__tabs">
            <TabView
              currentTime={currentTime}
              allNotes={allNotes}
              measureTimes={measureTimes}
              startDelay={startDelay}
              bpmScalingFactor={bpmScalingFactor}
            />
          </div>
        )}
        {showScore && (
          <div className="piece-viewer__score">
            <VerovioScoreViewer
              svgs={svgStrings}
              timemap={timemap}
              currentTime={currentTime - startDelay}
              onNoteClick={handleNoteClick}
              allNotes={allNotes}
              bpmScalingFactor={bpmScalingFactor}
              onAskStudyvarious={(measureNumber) => onAskStudyvarious(measureNumber, selectedPart, selectedPiece.id)}
              measureTimes={measureTimes}
            />
          </div>
        )}
      </div>
      <div className="piece-viewer__audio">
        <AudioPlayer
          ref={audioPlayerRef}
          pieceDisplayName={pieceDisplayName}
          midiBlob={midiBlob}
          isFavorited={isFavorited}
          selectedWork={selectedWork}
          addFavorite={addFavorite}
          removeFavorite={removeFavorite}
          bpm={bpm}
          selectedPart={selectedPart}
          handlePartSelection={handlePartSelection}
          parts={parts}
          selectedPiece={selectedPiece}
          onCurrentTimeChange={setCurrentTime}
          onBpmChange={setBpm}
          onNoteClick={handleNoteClick}
          isNotesLoading={isNotesLoading}
          fixed={!inlineAudioPlayer}
        />
      </div>
    </div>
  );
};

export default PieceViewer;